import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import {
  Form,
  Input,
  Button,
  FormFeedback,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import ProcessingSpinner from '../ProcessingSpinner';
import ShareHeader from './Header';
import ShareTitle from './Title';

import { cookieParams, isLoggedIn } from '../../api/Auth';

import { authShare } from '../../actions/Shares';

import { isValidEmail } from '../../helpers/Validators';

const ShareUnlockAccess = (({
  share,
  providePassword,
  onSubmit,
}) => {
  const intl = useIntl();

  const [form, setForm] = useState({
    passwordShare: '',
    email: '',
    name: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const requireName = !isLoggedIn() && share && share.require_name && !Cookies.get(`sp_share_user_name_${share.uuid}`);
  const requireEmail = !isLoggedIn() && share && share.require_email && !Cookies.get(`sp_share_user_email_${share.uuid}`);

  const validateForm = () => {
    const vErrors = {};

    if (providePassword && form.passwordShare.trim() === '') {
      vErrors.password = intl.formatMessage({ defaultMessage: 'Password is incorrect' });
    }

    if (requireEmail && !isValidEmail(form.email.trim())) {
      vErrors.email = intl.formatMessage({ defaultMessage: 'Enter a valid email address' });
    }

    if (requireName && form.name.trim() === '') {
      vErrors.name = intl.formatMessage({ defaultMessage: 'Enter your name' });
    }

    setErrors(vErrors);

    return Object.keys(vErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    const email = form.email.trim();
    const name = form.name.trim();
    const password = form.passwordShare;

    Cookies.set(`sp_share_user_email_${share.uuid}`, email, { ...cookieParams, expires: 7 });
    Cookies.set(`sp_share_user_name_${share.uuid}`, name, { ...cookieParams, expires: 7 });

    onSubmit(name, email, password);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setForm({ ...form, [name]: value });
  };

  let icon = '';
  if (share.require_password) {
    icon = 'lock';
  }
  if (
    share.require_name ||
    share.require_email
  ) {
    if (share.require_password) {
      icon = 'user-lock';
    } else {
      icon = 'user';
    }
  }

  const fields = [];
  if (requireName) {
    fields.push(intl.formatMessage({
      defaultMessage: 'name',
    }));
  }
  if (requireEmail) {
    fields.push(intl.formatMessage({
      defaultMessage: 'email',
    }));
  }
  if (share.require_password) {
    fields.push(intl.formatMessage({
      defaultMessage: 'password',
    }));
  }

  const text = intl.formatMessage({
    defaultMessage: `{fieldsCount, select,
      1 {Enter your {field1} to access this share}
      2 {Enter your {field1} and {field2} to access this share}
      3 {Enter your {field1}, {field2} and {field3} to access this share}
      other {Enter your details to access this share}}`,
  }, {
    fieldsCount: fields.length,
    field1: fields[0],
    field2: fields[1],
    field3: fields[2],
  });

  return (
    <div className="layout layout-share">
      <div className={classnames('share-main', {
        // 'with-actionbar': showingActionbar
      })}
      >
        <ShareHeader
          share={share}
        />
        <div className="share-content">
          <div className="share-list-header">
            <ShareTitle
              share={share}
            />
          </div>
          <div className="lock-section">
            <div className="lock-wrapper">
              <i className={`fa-regular fa-${icon}`} />
            </div>
            <h2>{text}</h2>
            <Form onChange={handleChange} onKeyPress={handleKeyPress}>
              <div className="access-input-container">
                {requireName && (
                  <div>
                    <Input
                      name="name"
                      id="inpName"
                      placeholder={intl.formatMessage({ defaultMessage: 'Enter your name' })}
                      invalid={!!errors.name}
                    />
                    <FormFeedback>
                      {errors.name}
                    </FormFeedback>
                  </div>
                )}
                {requireEmail && (
                  <div>
                    <Input
                      type="email"
                      name="email"
                      id="inpEmail"
                      placeholder={intl.formatMessage({ defaultMessage: 'Enter your email' })}
                      invalid={!!errors.email}
                    />
                    <FormFeedback>
                      {errors.email}
                    </FormFeedback>
                  </div>
                )}
                {providePassword && (
                  <div>
                    <Input
                      type="password"
                      name="passwordShare"
                      id="inpPasswordShare"
                      autoComplete="passwordShare"
                      onKeyPress={handleKeyPress}
                      placeholder={intl.formatMessage({ defaultMessage: 'Enter your password' })}
                    />
                    <FormFeedback>
                      {errors.password}
                    </FormFeedback>
                  </div>
                )}
                <div>
                  <Button
                    color="primary"
                    disabled={loading}
                    onClick={handleSubmit}
                  >
                    <ProcessingSpinner show={loading} /> {intl.formatMessage({ defaultMessage: 'Submit' })}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
});

const mapStateToProps = state => ({
  share: state.Shares.share,
  providePassword: state.Shares.providePassword,
});

const mapDispatchToProps = dispatch => ({
  performAuth: (shareUuid, params) => (
    dispatch(authShare(shareUuid, params))
  ),
});

ShareUnlockAccess.propTypes = {
  share: PropTypes.object.isRequired,
  providePassword: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShareUnlockAccess);
